import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import ImageViewer from '../components/ImageViewer';
import RequestPartModal from '../components/RequestPartModal';
import BrandLogo from '../components/BrandLogo';
import { useToast } from '../components/Toast';
import { colors, radii } from '../theme/tokens';
import Icon from '../components/ui/Icon';

const FindData = () => {
  const { user, token, refreshUserData } = useAuth();
  const showToast = useToast();

  const [view, setView] = useState('makes');
  const [selected, setSelected] = useState({ make: '', model: '', variant: '', from: '', to: '' });

  const [makes, setMakes] = useState([]);
  const [models, setModels] = useState([]);
  const [variants, setVariants] = useState([]);
  const [yearRangesByVariant, setYearRangesByVariant] = useState({});

  const [loadingMakes, setLoadingMakes] = useState(true);
  const [loadingModels, setLoadingModels] = useState(false);
  const [loadingVariants, setLoadingVariants] = useState(false);

  const [result, setResult] = useState(null);
  const [loadingParts, setLoadingParts] = useState(false);

  const [hoveredPart, setHoveredPart] = useState(null);
  const [selectedPartCode, setSelectedPartCode] = useState(null);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [showTokenPopup, setShowTokenPopup] = useState(false);
  const [pendingYearRange, setPendingYearRange] = useState(null);

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
  const isSilver = user?.tier === 'silver';

  useEffect(() => {
    fetchMakes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchMakes = async () => {
    try {
      setLoadingMakes(true);
      const response = await axios.get(`${API_URL}/api/car-list/makes-with-data`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMakes(response.data);
    } catch (error) {
      console.error('Failed to fetch makes:', error);
      const errorMsg = error.response?.data?.error || error.message || 'Unknown error';
      showToast('Failed to load car makes: ' + errorMsg, 'error');
    } finally {
      setLoadingMakes(false);
    }
  };

  const fetchModels = async (make) => {
    try {
      setLoadingModels(true);
      const response = await axios.get(`${API_URL}/api/car-list/models-with-data/${encodeURIComponent(make)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setModels(response.data);
    } catch (error) {
      console.error('Failed to fetch models:', error);
      showToast('Failed to load models for ' + make, 'error');
      setModels([]);
    } finally {
      setLoadingModels(false);
    }
  };

  const fetchVariantsAndYears = async (make, model) => {
    try {
      setLoadingVariants(true);
      const response = await axios.get(
        `${API_URL}/api/car-list/variants/${encodeURIComponent(make)}/${encodeURIComponent(model)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setVariants(response.data.variants || []);
      setYearRangesByVariant(response.data.yearRangesByVariant || {});
    } catch (error) {
      console.error('Failed to fetch variants:', error);
      setVariants([]);
      setYearRangesByVariant({});
    } finally {
      setLoadingVariants(false);
    }
  };

  const handleSelectMake = (make) => {
    setSelected({ make, model: '', variant: '', from: '', to: '' });
    setResult(null);
    fetchModels(make);
    setView('models');
  };

  const handleSelectModel = (model) => {
    setSelected(prev => ({ ...prev, model, variant: '', from: '', to: '' }));
    setResult(null);
    fetchVariantsAndYears(selected.make, model);
    setView('variants');
  };

  const handleSelectVariant = (variant) => {
    setSelected(prev => ({ ...prev, variant, from: '', to: '' }));
    setResult(null);
    setView('years');
  };

  const handleSelectYearRange = (yr) => {
    if (isSilver) {
      setPendingYearRange(yr);
      setShowTokenPopup(true);
    } else {
      fetchParts(yr);
    }
  };

  const confirmTokenAndFetch = () => {
    setShowTokenPopup(false);
    if (pendingYearRange) {
      fetchParts(pendingYearRange);
      setPendingYearRange(null);
    }
  };

  const cancelTokenPopup = () => {
    setShowTokenPopup(false);
    setPendingYearRange(null);
  };

  const fetchParts = async (yr) => {
    const params = {
      make: selected.make,
      model: selected.model,
      variant: selected.variant,
      from: yr.from,
      to: yr.to
    };
    setSelected(prev => ({ ...prev, from: yr.from, to: yr.to }));
    setLoadingParts(true);
    setView('parts');
    try {
      const res = await axios.get(`${API_URL}/api/cars/find`, {
        params,
        headers: { Authorization: `Bearer ${token}` }
      });
      setResult(res.data);
      await refreshUserData();
    } catch (err) {
      if (err.response?.status === 429) {
        showToast('Daily search limit reached! Upgrade to Gold for unlimited searches.', 'warning');
      } else if (err.response?.status === 404) {
        showToast('No parts found for this car configuration', 'info');
      } else {
        showToast('Search failed: ' + (err.response?.data?.error || 'Unknown error'), 'error');
      }
      setResult(null);
    } finally {
      setLoadingParts(false);
    }
  };

  // Breadcrumb navigation - return to a level and clear deeper selections
  const goToMakes = () => {
    setSelected({ make: '', model: '', variant: '', from: '', to: '' });
    setResult(null);
    setView('makes');
  };

  const goToModels = () => {
    setSelected(prev => ({ ...prev, variant: '', from: '', to: '' }));
    setResult(null);
    setView('models');
  };

  const goToVariants = () => {
    setSelected(prev => ({ ...prev, variant: '', from: '', to: '' }));
    setResult(null);
    setView('variants');
  };

  const goToYears = () => {
    setSelected(prev => ({ ...prev, from: '', to: '' }));
    setResult(null);
    setView('years');
  };

  const yearRangesForVariant = selected.variant ? (yearRangesByVariant[selected.variant] || []) : [];

  const renderBreadcrumb = () => {
    if (view === 'makes') return null;
    return (
      <div style={styles.breadcrumb}>
        <span style={styles.crumbLink} onClick={goToMakes}>
          <Icon name="home" size={16} /> Makes
        </span>
        {selected.make && (
          <>
            <span style={styles.crumbSep}>/</span>
            <span
              style={view === 'models' ? styles.crumbCurrent : styles.crumbLink}
              onClick={view === 'models' ? undefined : goToModels}
            >
              Make: {selected.make}
            </span>
          </>
        )}
        {selected.model && (
          <>
            <span style={styles.crumbSep}>/</span>
            <span
              style={view === 'variants' ? styles.crumbCurrent : styles.crumbLink}
              onClick={view === 'variants' ? undefined : goToVariants}
            >
              Model: {selected.model}
            </span>
          </>
        )}
        {selected.variant && (
          <>
            <span style={styles.crumbSep}>/</span>
            <span
              style={view === 'years' ? styles.crumbCurrent : styles.crumbLink}
              onClick={view === 'years' ? undefined : goToYears}
            >
              Variant: {selected.variant}
            </span>
          </>
        )}
        {selected.from && selected.to && (
          <>
            <span style={styles.crumbSep}>/</span>
            <span style={styles.crumbCurrent}>
              Year: {selected.from}-{selected.to}
            </span>
          </>
        )}
      </div>
    );
  };

  return (
    <div style={styles.container} className="container-mobile-padding">
      <h2 style={styles.title}><Icon name="search" size={28} color={colors.parGreenBright} /> Find Car Parts</h2>

      {isSilver && (
        <div style={styles.limitBanner}>
          <strong>Silver Tier Limit:</strong> {user.dailySearchCount} / 5 searches used today
          <br />
          <small>Upgrade to Gold for unlimited searches!</small>
        </div>
      )}

      {renderBreadcrumb()}

      {/* ── Makes grid ── */}
      {view === 'makes' && (
        <div style={styles.card} className="card-mobile-padding">
          <h3 style={styles.sectionTitle}>Select a Make</h3>
          {loadingMakes ? (
            <div style={styles.loading}>Loading makes...</div>
          ) : makes.length === 0 ? (
            <p style={styles.empty}>No makes with data yet.</p>
          ) : (
            <div style={styles.logoGrid}>
              {makes.map(make => (
                <button key={make} style={styles.logoCard} onClick={() => handleSelectMake(make)}>
                  <BrandLogo make={make} size={70} />
                  <span style={styles.logoLabel}>{make}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Models grid ── */}
      {view === 'models' && (
        <div style={styles.card} className="card-mobile-padding">
          <h3 style={styles.sectionTitle}>Select a Model</h3>
          {loadingModels ? (
            <div style={styles.loading}>Loading models...</div>
          ) : models.length === 0 ? (
            <p style={styles.empty}>No models with data for this make.</p>
          ) : (
            <div style={styles.cardGrid}>
              {models.map(model => (
                <button key={model} style={styles.textCard} onClick={() => handleSelectModel(model)}>
                  {model}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Variants grid ── */}
      {view === 'variants' && (
        <div style={styles.card} className="card-mobile-padding">
          <h3 style={styles.sectionTitle}>Select a Variant</h3>
          {loadingVariants ? (
            <div style={styles.loading}>Loading variants...</div>
          ) : variants.length === 0 ? (
            <p style={styles.empty}>No variants with data for this model.</p>
          ) : (
            <div style={styles.cardGrid}>
              {variants.map(variant => (
                <button key={variant} style={styles.textCard} onClick={() => handleSelectVariant(variant)}>
                  {variant}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Year ranges grid ── */}
      {view === 'years' && (
        <div style={styles.card} className="card-mobile-padding">
          <h3 style={styles.sectionTitle}>Select a Year Range</h3>
          {yearRangesForVariant.length === 0 ? (
            <p style={styles.empty}>No year ranges with data for this variant.</p>
          ) : (
            <div style={styles.cardGrid}>
              {yearRangesForVariant.map(yr => (
                <button
                  key={yr.display}
                  style={styles.textCard}
                  onClick={() => handleSelectYearRange(yr)}
                >
                  {yr.display}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Parts view ── */}
      {view === 'parts' && (
        <div style={styles.resultCard} className="card-mobile-padding">
          {loadingParts ? (
            <div style={styles.loading}>Loading parts...</div>
          ) : result && result.car ? (
            <>
              <h3 style={styles.resultTitle}>
                {result.car.make} {result.car.model} {result.car.variant}
              </h3>
              <p style={styles.yearRange}>
                Year Range: {result.car.yearRange.from} - {result.car.yearRange.to}
              </p>

              <h4 style={styles.partsTitle}>Parts ({result.car.parts.length}):</h4>
              <div style={styles.partsList}>
                {result.car.parts.map((part, i) => (
                  <div key={i} style={styles.partItem} className="grid-parts-result">
                    <span style={styles.partPosition}>{part.position}</span>
                    <span
                      style={styles.partCode}
                      onClick={() => setSelectedPartCode(part.code)}
                      title="Click to view product image"
                    >
                      {part.code}
                    </span>
                    <span style={styles.partQuantity}>
                      {part.quantity}{part.unit}
                    </span>
                    {(user.tier === 'gold' || user.tier === 'platinum') && part.addedBy && (
                      <div style={styles.userAttribution}>
                        <span
                          style={styles.userIcon}
                          onMouseEnter={() => setHoveredPart(i)}
                          onMouseLeave={() => setHoveredPart(null)}
                        >
                          <Icon name="account_circle" size={20} color={colors.textMuted} />
                          <span style={{
                            ...styles.tooltip,
                            visibility: hoveredPart === i ? 'visible' : 'hidden',
                            opacity: hoveredPart === i ? 1 : 0
                          }}>
                            <strong>{part.addedBy.name}</strong>
                            <br />
                            {new Date(part.createdAt).toLocaleDateString()}
                          </span>
                        </span>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {result.searchesRemaining !== 'unlimited' && (
                <div style={styles.remainingInfo}>
                  Searches remaining today: {result.searchesRemaining}
                </div>
              )}

              <button
                onClick={() => {
                  setShowRequestModal(true);
                }}
                style={styles.requestBtn}
              >
                <Icon name="photo_camera" size={18} /> Can't find your part? Request help
              </button>
            </>
          ) : (
            <>
              <p style={styles.empty}>No parts found for this car configuration.</p>
              <button
                onClick={() => setShowRequestModal(true)}
                style={styles.requestBtn}
              >
                <Icon name="photo_camera" size={18} /> Can't find your part? Request help
              </button>
            </>
          )}
        </div>
      )}

      {/* ── Token confirmation popup (Silver only) ── */}
      {showTokenPopup && (
        <div style={styles.modalOverlay} onClick={cancelTokenPopup}>
          <div style={styles.modalCard} onClick={e => e.stopPropagation()}>
            <div style={styles.modalIcon}><Icon name="paid" size={40} color={colors.parGreenBright} /></div>
            <h3 style={styles.modalTitle}>See Parts</h3>
            <p style={styles.modalText}>See parts for 1 token</p>
            <div style={styles.modalButtons}>
              <button style={styles.modalCancelBtn} onClick={cancelTokenPopup}>Cancel</button>
              <button style={styles.modalOkBtn} onClick={confirmTokenAndFetch}>OK</button>
            </div>
          </div>
        </div>
      )}

      {/* Image Viewer Modal */}
      {selectedPartCode && (
        <ImageViewer
          partCode={selectedPartCode}
          onClose={() => setSelectedPartCode(null)}
        />
      )}

      {/* Request Part Modal */}
      {showRequestModal && (
        <RequestPartModal
          initialMake={selected.make || ''}
          initialModel={selected.model || ''}
          initialVariant={selected.variant || ''}
          initialFrom={selected.from || ''}
          initialTo={selected.to || ''}
          showPhotoUpload={!!(selected.make && selected.variant)}
          onClose={() => setShowRequestModal(false)}
          onSuccess={() => {
            showToast('Request submitted! Our community will help find this part for you.', 'success');
          }}
        />
      )}
    </div>
  );
};

const glassCard = {
  background: colors.glass,
  border: `1px solid ${colors.glassBorder}`,
  padding: '28px',
  borderRadius: radii.lg,
  boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
  backdropFilter: 'blur(14px)',
  WebkitBackdropFilter: 'blur(14px)',
  color: colors.text
};

const tile = {
  padding: '18px 14px',
  background: colors.surfaceSoft,
  border: `1px solid ${colors.glassBorder}`,
  borderRadius: radii.md,
  cursor: 'pointer',
  fontSize: '15px',
  fontWeight: '600',
  color: colors.text,
  textAlign: 'center',
  transition: 'all 0.2s'
};

const styles = {
  container: { maxWidth: '900px', margin: '20px auto', padding: '20px' },
  title: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', color: colors.text, marginBottom: '20px' },
  limitBanner: { background: 'rgba(240,168,48,0.10)', color: colors.text, padding: '15px', borderRadius: radii.md, marginBottom: '20px', textAlign: 'center', borderLeft: `4px solid ${colors.amber}` },
  breadcrumb: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '6px',
    background: colors.glass,
    border: `1px solid ${colors.glassBorder}`,
    padding: '12px 16px',
    borderRadius: radii.md,
    marginBottom: '20px',
    fontSize: '14px'
  },
  crumbLink: { display: 'inline-flex', alignItems: 'center', gap: '4px', color: colors.parGreenBright, fontWeight: '600', cursor: 'pointer' },
  crumbCurrent: { color: colors.text, fontWeight: '700' },
  crumbSep: { color: colors.textFaint },
  card: { ...glassCard, marginBottom: '20px' },
  sectionTitle: { marginTop: 0, marginBottom: '20px', color: colors.text },
  logoGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
    gap: '15px'
  },
  logoCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    padding: '18px 10px',
    background: colors.white,
    border: `1px solid ${colors.glassBorder}`,
    borderRadius: radii.md,
    cursor: 'pointer',
    transition: 'all 0.2s',
    minHeight: '120px'
  },
  logoLabel: { fontSize: '13px', fontWeight: '700', color: '#333', textAlign: 'center' },
  cardGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
    gap: '12px'
  },
  textCard: tile,
  loading: { padding: '20px', textAlign: 'center', color: colors.textMuted },
  empty: { padding: '20px', textAlign: 'center', color: colors.textMuted },
  requestBtn: { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '12px', background: 'rgba(47,182,92,0.12)', color: colors.parGreenBright, border: `1px solid ${colors.parGreen}`, borderRadius: radii.pill, fontSize: '14px', fontWeight: '700', cursor: 'pointer', marginTop: '20px', width: '100%' },
  resultCard: glassCard,
  resultTitle: { color: colors.parGreenBright, marginBottom: '10px' },
  yearRange: { color: colors.textMuted, marginBottom: '20px' },
  partsTitle: { marginTop: '20px', marginBottom: '15px', color: colors.text },
  partsList: { display: 'flex', flexDirection: 'column', gap: '10px' },
  partItem: { display: 'grid', gridTemplateColumns: '2fr 2fr 1fr auto', gap: '15px', padding: '12px', background: colors.surfaceSoft, borderRadius: radii.sm, alignItems: 'center' },
  partPosition: { fontWeight: '600', color: colors.text },
  partCode: {
    color: colors.parGreenBright,
    fontFamily: 'monospace',
    cursor: 'pointer',
    textDecoration: 'underline',
    fontWeight: '600',
    transition: 'color 0.2s'
  },
  partQuantity: { textAlign: 'right', color: colors.textMuted },
  userAttribution: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
  userIcon: {
    position: 'relative',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    padding: '4px 8px',
    borderRadius: '50%',
    transition: 'background 0.2s'
  },
  tooltip: {
    visibility: 'hidden',
    opacity: 0,
    position: 'absolute',
    bottom: '100%',
    left: '50%',
    transform: 'translateX(-50%)',
    marginBottom: '8px',
    background: '#000',
    color: 'white',
    padding: '8px 12px',
    borderRadius: '6px',
    fontSize: '12px',
    whiteSpace: 'nowrap',
    zIndex: 1000,
    transition: 'opacity 0.2s',
    pointerEvents: 'none',
    boxShadow: '0 2px 8px rgba(0,0,0,0.4)'
  },
  remainingInfo: { marginTop: '20px', padding: '10px', background: 'rgba(58,155,212,0.12)', borderRadius: radii.sm, textAlign: 'center', color: colors.blue },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: colors.overlay,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 3000,
    padding: '20px',
    backdropFilter: 'blur(2px)'
  },
  modalCard: {
    background: colors.menuBg,
    border: `1px solid ${colors.glassBorder}`,
    borderRadius: radii.lg,
    padding: '30px',
    maxWidth: '360px',
    width: '100%',
    textAlign: 'center',
    color: colors.text,
    boxShadow: '0 20px 60px rgba(0,0,0,0.55)',
    backdropFilter: 'blur(16px)',
    WebkitBackdropFilter: 'blur(16px)'
  },
  modalIcon: { marginBottom: '10px' },
  modalTitle: { margin: '0 0 10px 0', color: colors.text },
  modalText: { margin: '0 0 24px 0', color: colors.textMuted, fontSize: '15px' },
  modalButtons: { display: 'flex', gap: '12px', justifyContent: 'center' },
  modalCancelBtn: {
    padding: '12px 24px',
    background: colors.glass,
    color: colors.text,
    border: `1px solid ${colors.glassBorder}`,
    borderRadius: radii.pill,
    fontSize: '15px',
    fontWeight: '700',
    cursor: 'pointer'
  },
  modalOkBtn: {
    padding: '12px 32px',
    background: colors.parGreen,
    color: 'white',
    border: 'none',
    borderRadius: radii.pill,
    fontSize: '15px',
    fontWeight: '700',
    cursor: 'pointer'
  }
};

export default FindData;
